<template>
  <v-expansion-panels v-if="state.isPublic" v-model="expandedSeating">
    <v-expansion-panel
      v-if="Object.keys(data.reservedBySection).length"
      value="reserved"
      :hide-actions="state.seatMapHasOnlyOneSection"
    >
      <v-expansion-panel-title class="justify-start">
        <v-icon class="mr-4">fal fa-chair</v-icon>
        <h1>RESERVED SEATING</h1>
      </v-expansion-panel-title>
      <v-expansion-panel-text class="pa-0">
        <v-expansion-panels
          v-model="expandedReservedSeatingSection"
          :hide-actions="onlyOneReservedTicketType"
        >
          <template
            v-for="(prices, sectionName) in data.reservedBySection"
            :key="sectionName"
          >
            <v-expansion-panel
              v-if="
                state.highlightedSectionName == sectionName ||
                !state.highlightedSectionName
              "
              :value="sectionName"
            >
              <v-expansion-panel-title class="section-title">
                {{ sectionName }}
              </v-expansion-panel-title>
              <v-expansion-panel-text class="pa-0">
                <v-list
                  class="pt-0"
                  aria-label="Reserved seating ticket options"
                >
                  <template
                    v-for="eventPrice in prices"
                    :key="eventPrice.id + '-select'"
                  >
                    <v-list-item
                      v-if="
                        !isAnotherRowSelected(
                          eventPrice.id,
                          sectionName as string,
                        )
                      "
                      class="pa-2"
                    >
                      <div class="seat-map-seats-row">
                        <div class="seat-map-seats-row-content">
                          <div>
                            <v-list-item-title>
                              <span class="text-wrap">{{
                                eventPrice.description
                              }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="!hideServiceFee">
                              {{ formatCurrency(eventPrice.price) }}
                              <div class="ml-3">
                                +
                                <span
                                  v-if="staticData.isPublicUser"
                                  class="text-no-wrap"
                                >
                                  {{ formatCurrency(eventPrice.serviceFee) }}
                                  Service Fee
                                </span>
                                <span v-else>
                                  {{
                                    formatCurrency(eventPrice.agentServiceFee)
                                  }}
                                  Agent Service Fee
                                </span>
                                <span
                                  ><br />
                                  +
                                  {{
                                    formatCurrency(
                                      eventPrice.convenienceFeeAmount ?? 0,
                                    )
                                  }}
                                  Conv. Fee</span
                                >
                                <span
                                  ><br />
                                  +
                                  {{
                                    formatCurrency(
                                      eventPrice.salesTaxAmount ?? 0,
                                    )
                                  }}
                                  Tax</span
                                >
                                <div>
                                  {{
                                    formatCurrency(eventPrice.totalSingle ?? 0)
                                  }}
                                  per ticket after taxes and fees
                                </div>
                              </div>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle v-else>
                              {{
                                formatCurrency(
                                  eventPrice.price! +
                                    (staticData.isPublicUser
                                      ? eventPrice.serviceFee!
                                      : eventPrice.agentServiceFee!),
                                )
                              }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              <span
                                v-if="eventPrice.minimumQuantity"
                                class="text-no-wrap"
                              >
                                Min Qty:
                                {{ eventPrice.minimumQuantity }}
                              </span>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              <span
                                v-if="eventPrice.maximumPurchaseQuantity"
                                class="text-no-wrap"
                              >
                                Max Qty:
                                {{ eventPrice.maximumPurchaseQuantity }}
                              </span>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              <span
                                v-if="shouldShowLowInventoryAlert(eventPrice)"
                                class="low-inventory-alert"
                              >
                                {{ handlers.getAvailableCount(eventPrice) }}
                                Remaining
                              </span>
                            </v-list-item-subtitle>
                          </div>
                          <div>
                            <v-list-item-action v-if="!isSjSeatMap">
                              <v-btn
                                variant="outlined"
                                :size="resolveXSmallComponentSize"
                                :color="
                                  state.highlightedPriceId === eventPrice.id &&
                                  state.highlightedSectionName === sectionName
                                    ? 'warning'
                                    : 'primary'
                                "
                                @click="
                                  handlers.handleSelectPrice(
                                    eventPrice,
                                    sectionName as string,
                                  )
                                "
                              >
                                {{
                                  state.highlightedPriceId === eventPrice.id &&
                                  state.highlightedSectionName === sectionName
                                    ? "Done"
                                    : "Select"
                                }}
                              </v-btn>
                            </v-list-item-action>
                          </div>
                        </div>
                      </div>
                    </v-list-item>
                  </template>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </template>
        </v-expansion-panels>
      </v-expansion-panel-text>
    </v-expansion-panel>
    <v-expansion-panel
      v-if="
        Object.keys(data.generalAdmissionBySection).length &&
        !isReservedSeatingRowSelected
      "
      :hide-actions="state.seatMapHasOnlyOneSection"
      value="general-admission"
    >
      <v-expansion-panel-title>
        <v-icon class="mr-4">fal fa-ticket</v-icon>
        <h1>GENERAL ADMISSION</h1>
      </v-expansion-panel-title>
      <v-expansion-panel-text class="pa-0">
        <v-expansion-panels
          v-model="expandedGASection"
          :hide-actions="onlyOneGATicketType"
        >
          <template
            v-for="(prices, sectionName) in data.generalAdmissionBySection"
            :key="sectionName"
          >
            <v-expansion-panel
              v-if="
                state.highlightedSectionName == sectionName ||
                !state.highlightedSectionName
              "
              :value="sectionName"
            >
              <v-expansion-panel-title class="section-title">
                {{ sectionName }}
              </v-expansion-panel-title>
              <v-expansion-panel-text class="pa-0">
                <v-list
                  class="pt-0"
                  aria-label="General admission ticket options"
                >
                  <template
                    v-for="eventPrice in prices"
                    :key="eventPrice.id + '-ga'"
                  >
                    <v-list-item :class="{ 'pa-2': isMobile }">
                      <purchase-quantity-selector-seat-map-ga-row
                        :eventPrice="eventPrice"
                        :purchase="data.purchase"
                        :sectionName="sectionName as string"
                        :sectionId="prices[0].seatMapSectionId"
                        :availableCount="handlers.getAvailableCount(eventPrice)"
                        :seatNumber="data.gaSectionSeats[sectionName as string]"
                        :highlightedPriceId="state.highlightedPriceId"
                        :highlightedSectionName="state.highlightedSectionName"
                        :isMobile="isMobile"
                        :staticData="staticData"
                        :hideServiceFee="hideServiceFee"
                        :handleGAQtyFocus="handlers.handleGAQtyFocus"
                        :handleSelectPrice="handlers.handleSelectPrice"
                      />
                    </v-list-item>
                  </template>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </template>
        </v-expansion-panels>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { formatCurrency } from "@common/utils";
import { EventPriceDto } from "@/models.g";
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import PurchaseQuantitySelectorSeatMapGaRow from "./purchase-quantity-selector-seat-map-ga-row.vue";
import useAccessibility from "@common/composables/useAccessibility";
import useStaticData from "@common/composables/useStaticData";

export interface SeatMapData {
  reservedBySection: Record<string, EventPriceDto[]>;
  generalAdmissionBySection: Record<string, EventPriceDto[]>;
  gaSectionSeats: Record<string, string>;
  purchase: TicketPurchaseDtoViewModel;
}

export interface SeatMapState {
  highlightedSectionName: string | null;
  highlightedPriceId: number | null;
  isPublic: boolean;
  seatMapHasOnlyOneSection: boolean;
}

export interface SeatMapHandlers {
  getAvailableCount: (eventPrice: EventPriceDto) => number | null;
  handleSelectPrice: (eventPrice: EventPriceDto, sectionName?: string) => void;
  handleGAQtyFocus: (eventPrice: EventPriceDto, sectionName: string) => void;
}

const { resolveXSmallComponentSize } = useAccessibility();
const staticData = useStaticData();
const display = useDisplay();

const expandedSeating = defineModel<string | null>("expandedSeating");
const expandedReservedSeatingSection = defineModel<string | null>(
  "expandedReservedSeatingSection",
);
const expandedGASection = defineModel<string | null>("expandedGASection");

const props = defineProps<{
  data: SeatMapData;
  state: SeatMapState;
  handlers: SeatMapHandlers;
  isSjSeatMap?: boolean;
}>();

const isMobile = computed(() => display.mdAndDown.value);

const hideServiceFee = computed(
  () =>
    (props.data.purchase.isPublic && props.data.purchase.hideServiceFee) ??
    false,
);

const onlyOneReservedTicketType = computed(
  () => Object.keys(props.data.reservedBySection).length === 1,
);

const onlyOneGATicketType = computed(
  () => Object.keys(props.data.generalAdmissionBySection).length === 1,
);

const isReservedSeatingRowSelected = computed(() => {
  if (
    props.state.highlightedPriceId === null ||
    props.state.highlightedSectionName === null
  ) {
    return false;
  }
  for (const [sectionName, prices] of Object.entries(
    props.data.reservedBySection,
  )) {
    if (props.state.highlightedSectionName === sectionName) {
      return prices.some(
        (price) => price.id === props.state.highlightedPriceId,
      );
    }
  }
  return false;
});

function isAnotherRowSelected(
  eventPriceId: number | null,
  sectionName?: string,
): boolean {
  return (
    props.state.highlightedPriceId !== null &&
    props.state.highlightedSectionName !== null &&
    (props.state.highlightedPriceId !== eventPriceId ||
      props.state.highlightedSectionName !== sectionName)
  );
}

function shouldShowLowInventoryAlert(eventPrice: EventPriceDto): boolean {
  const isIndividualSeatSection = Object.values(
    props.data.reservedBySection,
  ).some((prices) => prices.some((p) => p.id === eventPrice.id));
  if (isIndividualSeatSection) {
    return false;
  }
  const threshold = eventPrice.lowInventoryThreshold ?? 0;
  const available = props.handlers.getAvailableCount(eventPrice);
  const isSoldOut = eventPrice.isSoldOut ?? false;
  return (
    threshold > 0 &&
    available !== null &&
    available > 0 &&
    available <= threshold &&
    !isSoldOut
  );
}
</script>

<style scoped>
.section-title {
  background: #f5f8fa;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(60, 60, 60, 0.06);
  padding-left: 12px;
  letter-spacing: 0.01em;
}

@media (max-width: 960px) {
  .v-expansion-panel-title {
    font-size: inherit !important;
  }

  .v-expansion-panel {
    .v-expansion-panel-text {
      margin: 0px -10px -5px -10px;
    }
  }
}

.seat-map-seats-row {
  container-type: inline-size;
  container-name: seat-map-seats-row-container;
}

.seat-map-seats-row-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .v-list-item-subtitle {
    -webkit-line-clamp: inherit;
  }
  .v-list-item-title {
    white-space: normal;
  }
}

@container seat-map-seats-row-container (width > 322px) {
  .seat-map-seats-row-content {
    flex-direction: row;
    align-items: center;
  }
}

.v-list-item-action {
  /* overriding v-list-item-action default style */
  justify-content: center;
  margin-top: 0.5rem;
}

.low-inventory-alert {
  color: #1976d2 !important; /* Blue */
  font-weight: 700 !important; /* Bold */
  font-size: 0.9rem;
}

@media (width < 400px) {
  .seat-map-seats-row-content {
    display: flex;
    flex-direction: column;

    .v-list-item-subtitle {
      -webkit-line-clamp: inherit;
    }
    .v-list-item-title {
      white-space: normal;
    }
  }
}
</style>
